import type { ColumnDef } from '@tanstack/react-table';

import ChangeBadge from './change-badge';

import type { OverviewRow } from '../results-step.types';

const labelCellClass = 'text-base font-semibold text-gray-700 leading-6';
const valueCellClass = 'text-base text-gray-700 leading-6';

const commonHeaderStyles =
  'font-semibold uppercase cursor-default text-xs py-3';

interface OverviewTableSettingsParams {
  showModeledActualYear: boolean;
}

export const getOverviewTableSettings = ({
  showModeledActualYear,
}: OverviewTableSettingsParams): ColumnDef<OverviewRow>[] => {
  if (showModeledActualYear) {
    return [
      {
        id: 'metric',
        header: () => <span>Metric</span>,
        cell: ({ row }) => (
          <span className={labelCellClass}>{row.original.metric}</span>
        ),
        headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
        className: 'w-[18%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'actualYear',
        header: () => <span>Current Year</span>,
        cell: ({ row }) => (
          <span className={valueCellClass}>{row.original.actualYear}</span>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'modeledActualYear',
        header: () => <span>Modeled Current Year</span>,
        cell: ({ row }) => (
          <span className={valueCellClass}>
            {row.original.modeledActualYear}
          </span>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[20%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'modeledThisYear',
        header: () => <span>Modeled Future Year</span>,
        cell: ({ row }) => (
          <span className={valueCellClass}>{row.original.modeledThisYear}</span>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[20%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'percentChange',
        header: () => <span>% change</span>,
        cell: ({ row }) => (
          <ChangeBadge
            change={row.original.percentChange}
            data-testid={`results-percent-change-${row.original.key}`}
          />
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'volumeChange',
        header: () => <span>Vol change</span>,
        cell: ({ row }) => (
          <ChangeBadge
            change={row.original.volumeChange}
            data-testid={`results-volume-change-${row.original.key}`}
          />
        ),
        headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
    ];
  }

  return [
    {
      id: 'metric',
      header: () => <span>Metric</span>,
      cell: ({ row }) => (
        <span className={labelCellClass}>{row.original.metric}</span>
      ),
      headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
      className: 'w-[28%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'actualYear',
      header: () => <span>Current Year</span>,
      cell: ({ row }) => (
        <span className={valueCellClass}>{row.original.actualYear}</span>
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[18%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'modeledThisYear',
      header: () => <span>Modeled Future Year</span>,
      cell: ({ row }) => (
        <span className={valueCellClass}>{row.original.modeledThisYear}</span>
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[22%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'percentChange',
      header: () => <span>% change</span>,
      cell: ({ row }) => (
        <ChangeBadge
          change={row.original.percentChange}
          data-testid={`results-percent-change-${row.original.key}`}
        />
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[16%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'volumeChange',
      header: () => <span>Vol change</span>,
      cell: ({ row }) => (
        <ChangeBadge
          change={row.original.volumeChange}
          data-testid={`results-volume-change-${row.original.key}`}
        />
      ),
      headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
      className: 'w-[16%] px-2.5 py-2',
      enableSorting: false,
    },
  ];
};
